import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { DropOffCausesCard } from '~/app/landing/operator/service-quality/components/DropOffCausesCard/DropOffCausesCard';
import { useAuth } from '~/app/auth/context/AuthContext';
import { ChargingKpiService } from '~/app/services/charging-kpi/charging-kpi.service';
import { mapChargingKpiView } from '~/app/services/charging-kpi/map-charging-kpi-cards';
import type { DashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.types';
import type { KpiCardViewModel } from '../../OrganizationManagerDashboardPage.types';
import { isInitialDataLoad } from '../../utils';
import { KpiSummaryCard } from '../KpiSummaryCard/KpiSummaryCard';

export type ChargingKpisSectionProps = {
  period: DashboardPeriod;
  errorTitle: string;
  viewDetailsLabel: string;
  onViewDetails: (sectionId: string) => void;
};

function toSummaryCard(
  card: {
    id: string;
    title: string;
    metric: string;
    description: string;
    capacityLeft: string;
    capacityRight: string;
    progressPercent: number;
  } | null,
): KpiCardViewModel | null {
  return card
    ? {
        ...card,
        progressMode: 'single',
      }
    : null;
}

export function ChargingKpisSection({
  period,
  errorTitle,
  viewDetailsLabel,
  onViewDetails,
}: ChargingKpisSectionProps) {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const query = useQuery({
    queryKey: ['charging-kpis', organizationId, period],
    queryFn: () => ChargingKpiService.getKpis(period),
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });

  const chargingKpis = useMemo(
    () => (query.data ? mapChargingKpiView(query.data, t) : null),
    [query.data, t],
  );

  const sessionSuccessCard = toSummaryCard(
    chargingKpis?.sessionSuccess ?? null,
  );
  const weightedUptimeCard = toSummaryCard(
    chargingKpis?.weightedUptime ?? null,
  );
  const dropOffCard = chargingKpis?.dropOff ?? null;

  const showInitialLoader = isInitialDataLoad(query.isPending, query.data);

  if (showInitialLoader) {
    return (
      <Row gutter={[16, 16]} align="stretch">
        <Col span={24}>
          <Loader />
        </Col>
      </Row>
    );
  }

  if (query.isError) {
    const message =
      query.error instanceof Error
        ? query.error.message
        : t('errors.loadFallback');
    return (
      <Row gutter={[16, 16]} align="stretch">
        <Col span={24}>
          <ErrorBox title={errorTitle} description={message} />
        </Col>
      </Row>
    );
  }

  const isRefreshing = query.isFetching && query.isPlaceholderData;

  return (
    <Row
      gutter={[16, 16]}
      align="stretch"
      className={isRefreshing ? 'om-dashboard-section--refreshing' : undefined}
    >
      {sessionSuccessCard ? (
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <KpiSummaryCard
              card={sessionSuccessCard}
              viewDetailsLabel={viewDetailsLabel}
              onViewDetails={() => onViewDetails(sessionSuccessCard.id)}
            />
          </div>
        </Col>
      ) : null}
      {weightedUptimeCard ? (
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <KpiSummaryCard
              card={weightedUptimeCard}
              viewDetailsLabel={viewDetailsLabel}
              onViewDetails={() => onViewDetails(weightedUptimeCard.id)}
            />
          </div>
        </Col>
      ) : null}
      {dropOffCard ? (
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <DropOffCausesCard card={dropOffCard} />
          </div>
        </Col>
      ) : null}
    </Row>
  );
}
